import React, { Component } from "react";
import { Vector3 } from "three";

import * as THREE from "three";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";

import sceneHelper from "../../utils/helpers/sceneHelper";
import stringHelper from "../../utils/helpers/stringHelper";
import windowHelper from "../../utils/helpers/windowHelper";
import labelHelper from "../../utils/helpers/labelHelper";
import stateHelper from "../../utils/helpers/stateHelper";
import filterHelper from "../../utils/helpers/filterHelper";
import effectHelper from "../../utils/helpers/effectHelper";

import "./ObjectWindow.css";

class ObjectWindow extends Component {
  state = {
    camera: "",
    shouldPauseLoading: false,
    currentFloorLoading: "",
    isGIFShown: false,
    loadedImages: 0,
    shiftedApartmentKeys: [],
    allStates: [],
    imagesQueue: [],
    loadingQueue: [],
    cachedImages: {},
    labelIsHovered: false,
    hoveredHotspotKey: "",
    hoveredLinkHotspotUrl: "",
    mousePositionY: 0,
    loadingDone: false,
    shouldDisplayLabel: false,
    isSideMenuHovered: false,

    sceneNeedsRender: true,
    imageIsChanging: false,

    favouriteApartments: [],

    frameId: 0,
    frameParameters: {},
    defaultCameraTargetDistance: 0,

    fbxLoader: new FBXLoader(),
    loader: new THREE.TextureLoader(),
    mouse: new THREE.Vector2(9999, 9999),
    raycaster: new THREE.Raycaster(),
  };
  loadingClass = { display: "none" };

  async loadAndGetKeyframes() {
    for (let state of this.props.projectJSON.states) {
      if (state.hotspotUrl) {
        const keyframeTracks = await sceneHelper.loadCameraAndTargetAnimations(
          this.state.fbxLoader,
          state.hotspotUrl
        );
        return keyframeTracks;
      }
    }
    return [null, null];
  }

  /**
   * Function use to initialize three.js scene and attach event listeners related to three.js.
   */
  async initializeScene() {
    THREE.Cache.enabled = true;
    const scene = new THREE.Scene();
    const hotspotScene = new THREE.Scene();
    const renderer = sceneHelper.initializeRenderer("my-canvas");
    const camera = sceneHelper.initializeCamera();
    const sprite = sceneHelper.initializeSprite();
    const sphere = await sceneHelper.initializeBackground(
      this.props.threeSixtyImageUrl
    );

    const cameraAndTargetKeyframeTracks = await this.loadAndGetKeyframes();
    let cameraKeyframeTrack;
    let cameraTargetKeyframeTrack;
    if (!cameraAndTargetKeyframeTracks[0]) {
      cameraKeyframeTrack = null;
      cameraTargetKeyframeTrack = null;
    } else {
      cameraKeyframeTrack = cameraAndTargetKeyframeTracks[0];
      cameraTargetKeyframeTrack = cameraAndTargetKeyframeTracks[1];
    }

    camera.zoom = this.props.cameraZoom;

    const hotspotDictionary = await sceneHelper.loadHotspots(
      this.state.fbxLoader,
      hotspotScene,
      camera,
      this.props.projectJSON.states,
      scene,
      this.props
    );

    this.setState({ camera: camera });
    for (let i = 0; i < Object.keys(hotspotDictionary).length; i++) {
      hotspotScene.add(hotspotDictionary[i]);
    }

    // effectHelper.initializeOutlineRenderer(hotspotScene, camera, renderer);
    const outlineEffect = effectHelper.initializeOutlineRenderer(
      hotspotScene,
      camera,
      renderer
    );

    let label = await labelHelper.createLabel(
      this.props.apartmentHoverSquare,
      this.props.apartmentHoverTitle
    );

    scene.add(sprite);
    scene.add(sphere);

    window.addEventListener(
      "resize",
      () => {
        windowHelper.onWindowResize(renderer, camera);
      },
      false
    );

    window.addEventListener(
      "mousemove",
      (event) => {
        if (!this.state.loadingDone) return;
        this.onMouseMove(event, this.state.raycaster, this.state.mouse, camera);
      },
      false
    );

    window.addEventListener(
      "touchend",
      (event) => {
        this.onTouchEnd(event, this.state.raycaster, this.state.mouse, camera);
      },
      false
    );

    this.setState({
      frameParameters: {
        scene,
        hotspotScene,
        renderer,
        outlineEffect,
        camera,
        label,
        sprite,
        mouse: this.state.mouse,
        raycaster: this.state.raycaster,
        sphere,
        cameraKeyframeTrack,
        cameraTargetKeyframeTrack,
      },
      cameraDefaultPosition: camera.position.clone(),
    });
  }

  /**
   * Render loop.
   * @param scene
   * @param hotspotScene
   * @param renderer
   * @param outlineEffect
   * @param camera
   * @param label
   * @param mouse
   * @param raycaster
   * @param sphere
   */
  updateFrame = async (
    scene,
    hotspotScene,
    renderer,
    outlineEffect,
    camera,
    label,
    sprite,
    mouse,
    raycaster,
    sphere,
    cameraKeyframeTrack,
    cameraTargetKeyframeTrack
  ) => {
    let id = requestAnimationFrame(async () =>
      this.updateFrame(
        scene,
        hotspotScene,
        renderer,
        outlineEffect,
        camera,
        label,
        sprite,
        mouse,
        raycaster,
        sphere,
        cameraKeyframeTrack,
        cameraTargetKeyframeTrack
      )
    );

    this.setState({ frameId: id });
    if (this.state.labelIsHovered) return;

    this.setState({ hoveredHotspotKey: "" });

    this.setState({ shouldDisplayLabel: false });

    //Used to check if hotspots are interected
    if (
      hotspotScene.visible &&
      hotspotScene.children.length &&
      !this.props.isDragging &&
      !this.props.isMapShown &&
      !this.props.isCompareShown &&
      !this.props.isApartmentInfoShown
    ) {
      for (const child in hotspotScene.children) {
        let intersects = [];
        intersects = await raycaster.intersectObjects(
          hotspotScene.children[child].children
        );

        if (intersects.length === 0) {
          this.setState({ hoveredHotspotKey: "" });
        }

        if (
          intersects.length > 0 &&
          hotspotScene.children[child].visible &&
          intersects[0].object.visible
        ) {
          let intersect = intersects[0];
          this.setState({ hoveredHotspotKey: intersect.object.name });

          const intersectedApartment =
            this.props.allApartments[this.state.hoveredHotspotKey];

          if (intersectedApartment && intersectedApartment.isLink) {
            renderer.domElement.style.cursor = "pointer";
            this.setState({
              hoveredLinkHotspotUrl: intersectedApartment.linkTo,
            });
          }

          if (this.props.hotspotUnavailableColor !== undefined) {
            let color = new THREE.Color(this.props.hotspotUnavailableColor);
            if (intersects[0].object.material.color.equals(color)) {
              return;
            }
          }

          if (!this.props.isApartmentInfoShown) {
            labelHelper.updateHotspotInfo(
              intersect.object.name,
              this.props.currentState,
              this.props.favouriteApartments,
              this.props.projectJSON,
              this.props.apartmentReservedColor
            );
          }
          break;
        }

        if (intersects.length === 0) {
          if (this.state.hoveredLinkHotspotUrl !== "") {
            this.setState({ hoveredLinkHotspotUrl: "" });
          }
          hotspotScene.children[child].children.forEach((child) => {
            if (child.material) {
              child.material.opacity = 0;
              child.material.userData.outlineParameters = {
                visible: false,
              };
            }
          });
          renderer.domElement.style.cursor = "default";
        }

        if (this.state.hoveredLinkHotspotUrl) {
          break;
        }
      }
    }

    if (this.state.imageIsChanging) {
      return;
    }
    if (
      !this.state.hoveredLinkHotspotUrl &&
      this.state.hoveredHotspotKey &&
      !this.state.hoveredHotspotKey.includes("mask")
    ) {
      this.setState({ shouldDisplayLabel: true });

      labelHelper.moveLabel(this.state.mouse);
    }

    filterHelper.setCurrentPlaneVisibility(
      hotspotScene,
      this.state.hoveredHotspotKey,
      this.props.hotspots,
      this.props.hotspotUnavailableColor,
      this.props.currentTags,
      this.props.currentState,
      this.props.currentFilter,
      this.props.currentPricesFilter,
      this.props.currentQuadratureFilter,
      this.props.projectJSON.name ? this.props.projectJSON.states : null
    );
    this.applyCustomStateParameters(this.props.currentState);

    const cameraPosition = await sceneHelper.updateCameraPosition(
      camera,
      cameraKeyframeTrack,
      this.props.currentColumn,
      this.state.cameraDistanceMultiplier
    );

    if (cameraKeyframeTrack) {
      const targetPosition = await sceneHelper.updateTargetPosition(
        camera,
        cameraTargetKeyframeTrack,
        this.props.currentColumn,
        this.props.targetDefaultPosition,
        this.state.cameraDistanceMultiplier
      );

      const distance = await cameraPosition.distanceTo(targetPosition);

      if (
        this.state.defaultCameraTargetDistance === 0 ||
        this.props.calibratorEnabled
      ) {
        await this.setDefaultCameraDistance(
          cameraKeyframeTrack,
          cameraTargetKeyframeTrack
        );
      }

      sceneHelper.updateSpritePosition(
        sprite,
        cameraTargetKeyframeTrack,
        this.props.currentColumn,
        this.state.defaultCameraTargetDistance,
        distance,
        this.state.cameraDistanceMultiplier,
        targetPosition
      );
    }

    labelHelper.displayLabel(this.state.shouldDisplayLabel);
    this.scaleHotspots(this.state.hotspotScale, hotspotScene);

    camera.zoom = this.props.cameraZoom;
    renderer.clear();

    camera.updateProjectionMatrix();

    renderer.render(scene, camera);
    renderer.clearDepth();
    outlineEffect();
  };

  /**
   * Sets the default camera distance from the initial position of the animations.
   * @param cameraKeyframeTrack Keyframetrack containing camera positions
   * @param cameraTargetKeyframeTrack Keyframetrack containing camera target positions
   */
  async setDefaultCameraDistance(
    cameraKeyframeTrack,
    cameraTargetKeyframeTrack
  ) {
    const defaultCameraPosition = new Vector3(
      cameraKeyframeTrack.values[0],
      cameraKeyframeTrack.values[1],
      cameraKeyframeTrack.values[2]
    );

    let defaultCameraTargetPosition;
    if (cameraTargetKeyframeTrack !== undefined) {
      defaultCameraTargetPosition = await new Vector3(
        cameraTargetKeyframeTrack.values[0],
        cameraTargetKeyframeTrack.values[1],
        cameraTargetKeyframeTrack.values[2]
      );
    } else {
      defaultCameraTargetPosition = await this.props.targetDefaultPosition;
    }
    defaultCameraTargetPosition = defaultCameraTargetPosition
      .clone()
      .multiplyScalar(this.state.cameraDistanceMultiplier);

    const defaultCameraDistance = await defaultCameraPosition
      .multiplyScalar(this.state.cameraDistanceMultiplier)
      .distanceTo(defaultCameraTargetPosition);
    this.setState({ defaultCameraTargetDistance: defaultCameraDistance });
  }

  /**
   * Sets variables to state that are used to calibrate overlap of hotspots.
   * @param currentState Number of the current state
   */
  applyCustomStateParameters(currentState) {
    if (this.props.calibratorEnabled) {
      this.setState({
        cameraDistanceMultiplier: this.props.cameraDistanceMultiplier,
      });
      this.setState({ hotspotScale: this.props.hotspotScale });
      return;
    }

    const state = this.props.projectJSON.states[currentState];
    this.setState({ cameraDistanceMultiplier: state.cameraDistance });
    this.setState({ hotspotScale: state.hotspotSize });
  }

  /**
   * Scales the three.js scene containing the hotspots.
   * @param hotspotScale Scale variable used to scale the hotspot scene
   * @param hotspotScene Three.js scene containing the hotspots
   */
  scaleHotspots(hotspotScale, hotspotScene) {
    hotspotScene.scale.setScalar(hotspotScale);
  }

  /**
   * Shows loading gif.
   */
  showGIF() {
    const event = new CustomEvent("showGIF", {
      detail: {
        gifShown: true,
      },
    });
    let fullscreenNode = document.getElementById("full-screenable-node");
    fullscreenNode.dispatchEvent(event);

    this.setState({ isGIFShown: true });
    this.loadingClass = { display: "flex" };
  }
  /**
   * Hides loading gif.
   */
  hideGIF() {
    const event = new CustomEvent("showGIF", {
      detail: {
        gifShown: false,
      },
    });
    let fullscreenNode = document.getElementById("full-screenable-node");
    fullscreenNode.dispatchEvent(event);

    this.setState({ isGIFShown: false });
    this.loadingClass = { display: "none" };
    this.forceUpdate();
  }

  /**
   * Handles mousemove event for raycasting.
   * @param event Event parameter
   * @param raycaster Three.js raycaster
   * @param mouse Mouse position data
   * @param camera Three.js camera for the main scene
   */
  onMouseMove(event, raycaster, mouse, camera) {
    if (this.state.isSideMenuHovered) return;

    let container = document
      .getElementById("full-screenable-node")
      .getBoundingClientRect();

    this.setState({
      mouse: {
        // x: (event.clientX / window.innerWidth) * 2 - 1,
        // y: -(event.clientY / window.innerHeight) * 2 + 1,
        x: ((event.clientX - container.left) / container.width) * 2 - 1,
        y: -((event.clientY - container.top) / container.height) * 2 + 1,
        positionX: event.clientX - container.left,
        positionY: event.clientY - container.top,
      },
    });
    raycaster.setFromCamera(mouse, camera);
  }

  /**
   * Handles touchend event for raycasting.
   * @param event Event parameter
   * @param raycaster Three.js raycaster
   * @param mouse Mouse position data
   * @param camera Three.js camera for the main scene
   */
  onTouchEnd(event, raycaster, mouse, camera) {
    this.setState({
      mouse: {
        x: (event.changedTouches[0].clientX / window.innerWidth) * 2 - 1,
        y: -(event.changedTouches[0].clientY / window.innerHeight) * 2 + 1,
      },
    });
    raycaster.setFromCamera(mouse, camera);
  }

  /**
   * Texture loader that waits for image to download.
   * @param path Path where to load image from.
   */
  waitForImageLoad(path) {
    return new Promise((resolve, reject) => {
      this.state.loader.load(
        path,
        (texture) => {
          resolve(texture);
        },
        null,
        (error) => reject(error.error)
      );
    });
  }

  /**
   * Takes the n-th State from array and moves it to the start to be loaded.
   * @param  key Key of the State we are moving to the beginning.
   */
  shiftFloorToStart(key) {
    // Check if state with this key is already loaded.
    if (this.state.imagesQueue.indexOf(key) === -1) return;
    let indexOfKey = this.state.imagesQueue.indexOf(key);
    //if(indexOfKey === 0) return; // TODO: Test this for column changing it does not need to shift if its already loading this state.
    let imgKey = this.state.imagesQueue[indexOfKey];
    let imgPaths = this.state.loadingQueue[indexOfKey];

    this.state.imagesQueue.splice(indexOfKey, 1);
    this.state.loadingQueue.splice(indexOfKey, 1);

    this.state.imagesQueue.unshift(imgKey);
    this.state.loadingQueue.unshift(imgPaths);
  }

  /**
   * Given the position of the column, creates new array where the elements are in order: [column+i, column-i, ...]
   * @param key Key of the State.
   * @param column Number of Column.
   * @param shouldWait Should this method return promise.
   */
  shiftColumnToStart(key, column, shouldWait = true) {
    let indexOfState = this.state.imagesQueue.indexOf(key);
    let unsortedArray = this.state.loadingQueue[indexOfState];
    if (unsortedArray.length < 3) return unsortedArray; // <=
    let numberOfElements = unsortedArray.length;
    let sortedArray = [];
    let indexOfFirst = 0;

    let testArray = [];
    for (var i = 0; i < unsortedArray.length; i++) {
      testArray.push(unsortedArray[i][0]);
      if (unsortedArray[i][0] === column) {
        indexOfFirst = i;
      }
    }

    let indexOfPrevious = indexOfFirst - 1;
    let indexOfNext = indexOfFirst + 1;

    if (indexOfPrevious < 0) {
      indexOfPrevious = numberOfElements - 1;
    }

    if (indexOfNext >= numberOfElements) {
      indexOfNext = 0;
    }
    let currentElement = unsortedArray[indexOfFirst];
    let previousElement = unsortedArray[indexOfPrevious];
    let nextElement = unsortedArray[indexOfNext];

    sortedArray.push(currentElement);
    sortedArray.push(previousElement);
    sortedArray.push(nextElement);

    while (sortedArray.length !== numberOfElements) {
      indexOfPrevious--;
      indexOfNext++;

      if (indexOfPrevious < 0) indexOfPrevious = numberOfElements - 1;
      if (indexOfNext >= numberOfElements) indexOfNext = 0;

      previousElement = unsortedArray[indexOfPrevious];
      nextElement = unsortedArray[indexOfNext];

      sortedArray.push(previousElement);
      if (indexOfPrevious !== indexOfNext) sortedArray.push(nextElement);
    }

    this.state.loadingQueue[indexOfState] = sortedArray;
    if (shouldWait) {
      return new Promise((resolve) => resolve());
    }
  }

  /**
   * Starts loading Queue always loades the first element in Array.
   * After loading image, first element in array is removed.
   */
  async startLoadingQueue() {
    while (this.state.loadingQueue.length > 0) {
      this.state.currentFloorLoading = this.state.imagesQueue[0];

      if (this.state.loadingQueue[0][0] === undefined) {
        console.warn("this.state.loadingQueue[0][0] is undefiend");
        this.state.loadingQueue[0].shift();
        if (this.state.loadingQueue[0].length === 0) {
          this.state.loadingQueue.shift();
          this.state.imagesQueue.shift();
        }
        continue;
      }

      let path = this.state.loadingQueue[0][0][1];
      let key =
        this.state.currentFloorLoading + "_" + this.state.loadingQueue[0][0][0];

      let buildingOrApartmentIdentifier =
        stringHelper.getLoadingStateIdentifier(key);

      let texture = await this.waitForImageLoad(path);
      texture.minFilter = THREE.LinearFilter;

      this.state.cachedImages[key] = texture;

      let currentKey = stringHelper.makeImageKey(
        buildingOrApartmentIdentifier === "B" ? "B" : "A",
        "High",
        this.props.currentState,
        buildingOrApartmentIdentifier === "B"
          ? this.props.currentRow
          : this.props.apartmentClickedInfo !== undefined
          ? this.props.apartmentClickedInfo.key
          : "",
        buildingOrApartmentIdentifier === "B"
          ? this.props.currentColumn
          : this.props.currentHotspotColumn
      );

      if (this.state.currentFloorLoading === this.state.imagesQueue[0]) {
        this.state.loadingQueue[0].shift();
        if (this.state.loadingQueue[0].length === 0) {
          this.state.loadingQueue.shift();
          this.state.imagesQueue.shift();
        }
        if (key === currentKey) {
          if (buildingOrApartmentIdentifier === "B")
            sceneHelper.updateTexture(texture);
          else sceneHelper.updateApartmentTexture(texture);
          this.setState({ sceneNeedsRender: true });

          if (this.state.isGIFShown) this.hideGIF();
        }
        if (this.state.shouldPauseLoading === true) {
          await this.shiftColumnToStart(
            this.state.imagesQueue[0],
            this.props.currentColumn,
            true
          );
          this.state.shouldPauseLoading = false;
        }
      } else {
        let loadingStateIndex = this.state.imagesQueue.indexOf(
          this.state.currentFloorLoading
        );
        // We shifted the new state to be loaded first => pop this state from queue(Where).
        this.state.loadingQueue[loadingStateIndex].shift();
        // If we loaded all images from the second state => second because it was shifted in the mean time.
        if (this.state.loadingQueue[loadingStateIndex].length === 0) {
          this.state.loadingQueue.splice(loadingStateIndex, 1);
        }
        if (this.state.shouldPauseLoading === true) {
          await this.shiftColumnToStart(
            this.state.imagesQueue[0],
            this.props.currentColumn,
            true
          );
          this.state.shouldPauseLoading = false;
        }
      }
    }
    this.setState({ currentFloorLoading: "" });
  }

  /**
   * Loads, stores default state images and update the proggress for loading bar.
   */
  async loadDefaultStateImages() {
    let floor = this.props.currentState;
    let rows = this.props.projectJSON.states[floor].rows.length;
    let columns = this.props.maxColumns;

    let imagesToLoad = rows * columns;
    let loadedImages = 0;

    for (var i = 0; i < rows; i++) {
      for (var j = 0; j < columns; j++) {
        let textureKey = stringHelper.makeImageKey("B", "High", floor, i, j);
        let path = this.state.loadingQueue[0][0][1];

        let texture = await this.waitForImageLoad(path);
        texture.minFilter = THREE.LinearFilter;
        this.state.cachedImages[textureKey] = texture;

        loadedImages++;

        let percentage = Math.floor((loadedImages / imagesToLoad) * 100);
        this.props.update.getLoadingProgress(percentage);
        this.state.loadingQueue[0].shift();
      }
      this.state.imagesQueue.shift();
      this.state.loadingQueue.shift();
    }

    let key = stringHelper.makeImageKey(
      "B",
      "High",
      floor,
      this.props.currentRow,
      this.props.currentColumn
    );

    //Apply Apartment Image in Current State
    this.applyBuildingImageInCurrentState(key);
    this.setState({ loadingDone: true });
    //Start LoadingQueue
    this.startLoadingQueue();
  }

  /**
   *
   * @param parameters
   * @param lowImageKey
   * @param path
   * @param isBuilding
   */
  async loadLowResAndApply(parameters, lowImageKey, path, isBuilding = true) {
    this.setState({ imageIsChanging: true });
    // If High res image with same parametars is loaded in meantime.
    let checkHighKey = "";

    checkHighKey = stringHelper.makeImageKey(
      isBuilding ? "B" : "A",
      "High",
      parameters[0],
      parameters[1],
      parameters[2]
    );

    if (this.state.cachedImages[checkHighKey] !== undefined) {
      if (this.state.isGIFShown) this.hideGIF();
    }
    if (!this.state.isGIFShown) this.showGIF();

    let fullscreenNode = document.getElementById("full-screenable-node");
    let event = new CustomEvent("imageIsLoading", {
      detail: {
        value: true,
      },
    });
    fullscreenNode.dispatchEvent(event);

    let texture = await this.waitForImageLoad(path);
    // Apply Texture
    texture.minFilter = THREE.LinearFilter;

    // Cache this low res
    this.state.cachedImages[lowImageKey] = texture;
    let currentKey = "";

    currentKey = stringHelper.makeImageKey(
      isBuilding ? "A" : "B",
      "Low",
      this.props.currentState,
      this.props.currentRow,
      this.props.currentColumn
    );

    // If current key is not at the same as this key.
    // Or the high res image is loaded in the meantime.
    if (
      lowImageKey !== currentKey ||
      this.state.cachedImages[checkHighKey] !== undefined
    ) {
      event = new CustomEvent("imageIsLoading", {
        detail: {
          value: false,
        },
      });
      fullscreenNode.dispatchEvent(event);

      if (this.state.isGIFShown) this.hideGIF();
    }
    if (isBuilding) sceneHelper.updateTexture(texture);
    else sceneHelper.updateApartmentTexture(texture);

    this.setState({ sceneNeedsRender: true });
    this.setState({ imageIsChanging: false });

    if (this.state.isGIFShown) this.hideGIF();

    event = new CustomEvent("imageIsLoading", {
      detail: {
        value: false,
      },
    });
    fullscreenNode.dispatchEvent(event);
  }

  /**
   * Finds the building texture with key from cache and sends it to the event.
   * @param key State Building key
   */
  async applyBuildingImageInCurrentState(key) {
    if (this.state.cachedImages[key] !== undefined) {
      sceneHelper.updateTexture(this.state.cachedImages[key]);
      this.setState({ sceneNeedsRender: true });
    }
  }

  /**
   * Finds the apartment texture with key from cache and sends it to the event.
   * @param key State Apartment key
   */
  applyApartmentImageInCurrentState(key) {
    if (this.state.cachedImages[key] !== undefined) {
      sceneHelper.updateApartmentTexture(this.state.cachedImages[key]);
    }
  }

  /**
   * Handles when props change, applies texture for this state or if there are is no texture => Load it.
   * @param newProps
   */
  async changeFloor(newProps) {
    const floor = newProps.currentState;
    const row = newProps.currentRow;
    const column = newProps.currentColumn;
    let stateKey = stringHelper.makeStateKey("B", "High", floor, row);
    let imageKey = stateKey + "_" + column;

    if (stateHelper.isStateLoaded(this.state.imagesQueue, stateKey))
      this.applyBuildingImageInCurrentState(imageKey);
    else {
      // Check if image with these parametars is already loaded.
      if (this.state.cachedImages[imageKey] !== undefined)
        this.applyBuildingImageInCurrentState(imageKey);
      // Image with these parametars is not loaded.
      else {
        let lowImageKey = stringHelper.makeImageKey(
          "B",
          "Low",
          floor,
          row,
          column
        );
        if (this.state.cachedImages[lowImageKey] !== undefined)
          this.applyBuildingImageInCurrentState(lowImageKey);
        // Load Low Res Image.
        else {
          let lowImagePath =
            this.props.projectJSON.states[floor].rows[row].lowResUrls[column];
          await this.loadLowResAndApply(
            [floor, row, column],
            lowImageKey,
            lowImagePath
          );
          if (this.state.cachedImages[imageKey] === undefined) {
            // This state is Already loading
            if (this.state.currentFloorLoading === stateKey) {
              // Pause async loading so that columns can be shifted
              this.state.shouldPauseLoading = true;
            } else {
              this.shiftColumnToStart(
                stateKey,
                this.props.currentColumn,
                false
              );
            }
          }
        }
      }
      // Shift this state to beginning of the queue, to be loaded.
      this.shiftFloorToStart(stateKey);
    }
  }

  /**
   * Handles when props change, applies texture for this state or if there are is no texture => Load it.
   * @param newProps
   */
  async changeRow(newProps) {
    const floor = newProps.currentState;
    const row = newProps.currentRow;
    const column = newProps.currentColumn;

    let stateKey = stringHelper.makeStateKey("B", "High", floor, row);
    let imageKey = stateKey + "_" + column;

    if (stateHelper.isStateLoaded(this.state.imagesQueue, stateKey)) {
      this.applyBuildingImageInCurrentState(imageKey);
    } else {
      // Check if high image with these parametars is already loaded.
      if (this.state.cachedImages[imageKey] !== undefined) {
        this.applyBuildingImageInCurrentState(imageKey);
      }
      // High Image with these parametars is not loaded.
      else {
        let lowImageKey = stringHelper.makeImageKey(
          "B",
          "Low",
          floor,
          row,
          column
        );
        if (this.state.cachedImages[lowImageKey] !== undefined) {
          this.applyBuildingImageInCurrentState(lowImageKey);
        } else {
          let lowImagePath =
            this.props.projectJSON.states[floor].rows[row].lowResUrls[column];
          await this.loadLowResAndApply(
            [floor, row, column],
            lowImageKey,
            lowImagePath
          );
        }
      }

      // This state is Already loading
      if (this.state.currentFloorLoading === stateKey) {
        // Pause async loading so that columns could be shifted
        this.state.shouldPauseLoading = true;
      }
      // Shift this state to beginning of the queue, to be loaded.
      this.shiftFloorToStart(stateKey);

      //this.shiftFloorToStart(stateKey);
    }
  }

  /**
   * Handles when props change, applies texture for this state or if there are is no texture => Load it.
   * @param newProps
   */
  async changeColumn(newProps) {
    const floor = newProps.currentState;
    const row = newProps.currentRow;
    const column = newProps.currentColumn;
    let stateKey = stringHelper.makeStateKey("B", "High", floor, row);
    let imageKey = stateKey + "_" + column;

    // Whole state is loaded
    if (stateHelper.isStateLoaded(this.state.imagesQueue, stateKey)) {
      this.applyBuildingImageInCurrentState(imageKey);
    } else {
      // Check if image with these parametars is already loaded.
      if (this.state.cachedImages[imageKey] !== undefined) {
        // Apply High Res image.
        this.applyBuildingImageInCurrentState(imageKey);
      }
      // Image with these parametars is not loaded.
      else {
        // Load Low Res Image.
        let lowImageKey = stringHelper.makeImageKey(
          "B",
          "Low",
          floor,
          row,
          column
        );
        if (this.state.cachedImages[lowImageKey] !== undefined) {
          this.applyBuildingImageInCurrentState(lowImageKey);
        } else {
          let lowImagePath =
            this.props.projectJSON.states[floor].rows[row].lowResUrls[column];
          await this.loadLowResAndApply(
            [floor, row, column],
            lowImageKey,
            lowImagePath
          );
        }
        // Shift this state to beginning of the queue, to be loaded.
        //this.shiftFloorToStart(stateKey);
      }
    }
  }

  /**
   * Initializes render loop for main scene/canvas.
   */
  initializeRenderLoop = () => {
    let parameters = this.state.frameParameters;
    requestAnimationFrame(() => {
      this.updateFrame(
        parameters.scene,
        parameters.hotspotScene,
        parameters.renderer,
        parameters.outlineEffect,
        parameters.camera,
        parameters.label,
        parameters.sprite,
        parameters.mouse,
        parameters.raycaster,
        parameters.sphere,
        parameters.cameraKeyframeTrack,
        parameters.cameraTargetKeyframeTrack
      );
    });
  };

  /**
   * Handles the event for updating apartment texture
   * @param parameters Current State, Apartmnet Key, Current Hotspot Column, Low img url
   */
  handleUpdateApartmentTexture(parameters) {
    let key = stringHelper.makeImageKey(
      "A",
      "High",
      parameters[0],
      parameters[1],
      parameters[2]
    );
    if (this.state.cachedImages[key] === undefined) {
      let lowKey = stringHelper.makeImageKey(
        "A",
        "Low",
        parameters[0],
        parameters[1],
        parameters[2]
      );
      // If we have low res iamge with this key cached => show it.
      if (this.state.cachedImages[lowKey] === undefined) {
        this.loadLowResAndApply(parameters, lowKey, parameters[3], false);
      } else {
        sceneHelper.updateApartmentTexture(this.state.cachedImages[lowKey]);
      }
    } else {
      sceneHelper.updateApartmentTexture(this.state.cachedImages[key]);
    }
  }

  componentWillReceiveProps(newProps) {
    let floor = this.props.currentState;
    let row = this.props.currentRow;
    let column = this.props.currentColumn;

    if (column !== newProps.currentColumn) {
      this.changeColumn(newProps);
    } else if (floor !== newProps.currentState) {
      this.changeFloor(newProps);
    } else if (row !== newProps.currentRow) {
      this.changeFloor(newProps);
    }
  }

  async componentDidMount() {
    let array = await stateHelper.initializeStartingQueue();
    this.setState({ allStates: array[1] });

    this.setState({
      loadingQueue: array[0],
      imagesQueue: array[1],
    });

    this.loadDefaultStateImages();

    const fullscreenWrapper = document.getElementById("full-screenable-node");

    if (this.props.menuVisibility) {
      const sideMenu = document.getElementById("side-menu");

      sideMenu.addEventListener("mouseover", () => {
        this.setState({ isSideMenuHovered: true });
      });

      sideMenu.addEventListener("mouseout", () => {
        this.setState({ isSideMenuHovered: false });
      });
    }

    fullscreenWrapper.addEventListener("labelMouseOut", (event) => {
      this.setState({
        labelIsHovered: false,
        hoveredHotspotKey: "",
      });
    });

    fullscreenWrapper.addEventListener("labelIsHovered", (event) => {
      this.setState({ labelIsHovered: true });
    });

    fullscreenWrapper.addEventListener("renderMainCanvas", (event) => {
      let parameters = this.state.frameParameters;
      requestAnimationFrame(() => {
        this.updateFrame(
          parameters.scene,
          parameters.hotspotScene,
          parameters.renderer,
          parameters.outlineEffect,
          parameters.camera,
          parameters.label,
          parameters.sprite,
          parameters.mouse,
          parameters.raycaster,
          parameters.sphere,
          parameters.cameraKeyframeTrack,
          parameters.cameraTargetKeyframeTrack
        );
      });
    });

    window.addEventListener("touchend", () => {
      this.setState({ sceneNeedsRender: true });
    });
    window.addEventListener("mousemove", (event) => {
      if (this.state.labelIsHovered) return;
      this.state.mousePositionY = event.clientY;
    });

    window.addEventListener("setupDefaultState", async () => {
      await this.initializeScene();
      await this.initializeRenderLoop();
    });

    window.addEventListener("apartmentInfoOff", (event) => {
      let stateKey = stringHelper.makeStateKey(
        "B",
        "High",
        this.props.currentState,
        this.props.currentRow
      );
      if (!stateHelper.isStateLoaded(this.state.imagesQueue, stateKey)) {
        if (this.state.currentFloorLoading === stateKey) {
          // Shift this one to be loaded.
          this.state.shouldPauseLoading = true;
          this.shiftFloorToStart(stateKey);
        } else {
          this.shiftFloorToStart(stateKey);
        }
      }
    });

    window.addEventListener("LoadingFinished", (e) => {
      const favouriteButton = document.getElementById("favourite-button");
      const moreDetailsButton = document.getElementById("details-button");
      const hiddenMoreDetailsButton = document.getElementById(
        "hidden-details-button"
      );

      window.addEventListener("click", () => {
        if (this.state.hoveredLinkHotspotUrl !== "")
          window.top.location.href = this.state.hoveredLinkHotspotUrl;
      });

      favouriteButton.addEventListener("click", (e) => {
        let favouriteApartments = this.props.favouriteApartments;
        const apartmentKey = this.state.hoveredHotspotKey;

        if (favouriteApartments.includes(apartmentKey)) {
          let event = new CustomEvent("removeFavourite", {
            detail: {
              key: apartmentKey,
            },
          });

          window.dispatchEvent(event);

          favouriteButton.style.backgroundImage =
            "url('design/new-exports/Star_with_plus.png')";
          return;
        }

        favouriteButton.style.backgroundImage =
          "url('design/new-exports/Star_with_minus.png')";

        let event = new CustomEvent("addFavourite", {
          detail: {
            key: apartmentKey,
          },
        });

        window.dispatchEvent(event);
      });

      moreDetailsButton.addEventListener("click", async (e) => {
        let apartmentKey = stringHelper.makeStateKey(
          "A",
          "High",
          this.props.currentState,
          this.state.hoveredHotspotKey
        );

        if (this.state.imagesQueue.includes(apartmentKey)) {
          if (
            !this.state.shiftedApartmentKeys.includes(apartmentKey) &&
            this.state.allStates.includes(apartmentKey)
          ) {
            await this.shiftColumnToStart(apartmentKey, 0, true);
            let shiftedApartmentKeys = this.state.shiftedApartmentKeys;
            shiftedApartmentKeys.push(apartmentKey);
            this.setState({ shiftedApartmentKeys });
          }
          if (this.state.allStates.includes(apartmentKey)) {
            this.shiftFloorToStart(apartmentKey);
          }
        }
        let event = new CustomEvent("openApartmentWindow", {
          detail: {
            apartmentKey: this.state.hoveredHotspotKey,
          },
        });
        fullscreenWrapper.dispatchEvent(event);
      });

      hiddenMoreDetailsButton.addEventListener("click", (e) => {
        let apartmentKey = stringHelper.makeStateKey(
          "A",
          "High",
          this.props.currentState,
          this.state.hoveredHotspotKey
        );
        if (this.state.imagesQueue.includes(apartmentKey)) {
          if (
            !this.state.shiftedApartmentKeys.includes(apartmentKey) &&
            this.state.allStates.includes(apartmentKey)
          ) {
            this.shiftColumnToStart(apartmentKey, 0, false);
            let shiftedApartmentKeys = this.state.shiftedApartmentKeys;
            shiftedApartmentKeys.push(apartmentKey);

            this.setState({ shiftedApartmentKeys });
          }
          if (this.state.allStates.includes(apartmentKey))
            this.shiftFloorToStart(apartmentKey);
        }
        let event = new CustomEvent("openApartmentWindow", {
          detail: {
            apartmentKey: this.state.hoveredHotspotKey,
          },
        });
        fullscreenWrapper.dispatchEvent(event);
      });
    });

    window.addEventListener("updateApartmentTexture", (event) => {
      this.handleUpdateApartmentTexture(event.detail.parameters);
    });
  }

  render() {
    return (
      <div style={this.loadingClass} className="loading-image-overlay">
        <div className="loader-image-gif">
          <img
            src={`${
              this.props.loaderImg
                ? this.props.loaderImg
                : "/design/new-exports/loading_animation.svg"
            }`}
            alt=""
          />
        </div>
      </div>
    );
  }
}

export default ObjectWindow;
